import { Component } from "@theme/component";
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
} from "@theme/utilities";
import { morphSection, sectionRenderer } from "@theme/section-renderer";
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from "@theme/events";
import { cartPerformance } from "@theme/performance";

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener("refresh-cart", this.#handleCartUpdate);
    document.addEventListener(
      ThemeEvents.discountUpdate,
      this.handleDiscountUpdate
    );
    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#debouncedOnChange
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      ThemeEvents.cartUpdate,
      this.#handleCartUpdate
    );
    document.removeEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#debouncedOnChange
    );
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: "change",
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (
      lineItemRow.querySelector("text-component")
    );
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: "clear",
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const remove = () => cartItemRowToRemove.remove();

    if (prefersReducedMotion()) return remove();

    // Add class to the row to trigger the animation
    cartItemRowToRemove.style.setProperty(
      "--row-height",
      `${cartItemRowToRemove.clientHeight}px`
    );
    cartItemRowToRemove.classList.add("removing");

    // Remove the row after the animation ends
    onAnimationEnd(cartItemRowToRemove, remove);
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(
      `${config.action}:user-action`
    );

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll(
      "cart-items-component"
    );
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(","),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig("json", { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          "text/html"
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector(
          '[ref="cartItemCount"]'
        )?.textContent;
        const newCartItemCount = newCartHiddenItemCount
          ? parseInt(newCartHiddenItemCount, 10)
          : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: "cart-items-component",
            sections: parsedResponseText.sections,
          })
        );

        morphSection(
          this.sectionId,
          parsedResponseText.sections[this.sectionId]
        );
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector("input");

    if (!quantityInput) throw new Error("Quantity input not found");

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement))
      throw new Error("Cart item error not found");
    if (!(cartItemErrorContainer instanceof HTMLElement))
      throw new Error("Cart item error container not found");

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove("hidden");
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }

    if (event.target === this) return;

    const cartMode = document.body.dataset.cartMode;
    const cartItemsHtml = event.detail?.data?.sections?.[this.sectionId];
    const productId = event.detail?.sourceId;
    const productIds = event.detail?.sourceIds;

    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml).then(() => {
        if (cartMode === "notification") {
          this.processCartNotification(cartItemsHtml, productIds, productId)
          return;
        }
      });
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false }).then((response) => {
        if (cartMode === "notification") {
          this.processCartNotification(response, productIds, productId)
          return;
        }
      });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add("cart-items-disabled");
  }

  processCartNotification(html, productIds, productId) {
    if (productIds && productIds.length > 0) {
      this.showCartNotification(html, productIds);
    }
    else if (productId) {
      this.showCartNotification(html, [productId]);
    }
  }

  showCartNotification(html, productIds) {
    this.productIds = productIds;
    const parsed = new DOMParser().parseFromString(html, "text/html");

    this.getSectionsToRender().forEach((section) => {
      const container = document.getElementById(section.id);
      if (!container) return;

      let newContent = '';

      if (section.id == 'cart-notification-products') {
        productIds.forEach(productId => {
          const dynamicSelector = `[id="cart-product-${productId}"]`;
          const element = parsed.querySelector(dynamicSelector);
          if (element) {
            newContent += element.outerHTML;
          }
        });
      } else {
        if (section.selector) {
          const element = parsed.querySelector(section.selector);
          if (element) {
            newContent = element.outerHTML;
          }
        } else {
          const element = parsed.getElementById(section.id);
          if (element) {
            newContent = element.outerHTML;
          }
        }
      }

      if (newContent) {
        container.innerHTML = newContent;
      }
    });
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-products',
        selector: `[id="cart-product-${this.productId}"]`,
      },
      {
        id: 'cart-button',
      }
    ];
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove("cart-items-disabled");
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error("Section id missing");

    return sectionId;
  }
}

if (!customElements.get("cart-items-component")) {
  customElements.define("cart-items-component", CartItemsComponent);
}

// Global event delegation — handles dynamic content reliably
document.addEventListener("click", (event) => {
  const addUpsell = event.target.closest(".js-add-upsell");
  if (addUpsell) {
    handleAddUpsell(addUpsell);
    return;
  }
});

async function handleAddUpsell(addUpsell) {
  const variantId = addUpsell?.dataset.variantId;
  const productId = addUpsell?.dataset.productId;

  if (!variantId && !productId) {
    console.error("Upsell Product: Variant ID missing");
    return;
  }

  const spinner = addUpsell.querySelector(".spinner-wrapper");
  const plusIcon = addUpsell.querySelector(".icon-plus");

  if (spinner) spinner.hidden = false;
  if (plusIcon) plusIcon.hidden = true;

  try {
    await refreshCart();

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set();

    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    formData.append('sections', Array.from(sectionsToUpdate).join(','));

    const fetchCfg = fetchConfig('javascript', { body: formData });

    const response = await fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    }).then((response) => response.json());

    if (response.status) {
      console.error('Upsell add error:', response.message);

      document.dispatchEvent(new CartAddEvent({}, variantId, {
        didError: true,
        source: 'upsell-component',
        itemCount: 1,
        variantId: variantId,
        productId: productId,
        message: response.message
      }));

    } else {
      document.dispatchEvent(new CartAddEvent({}, variantId, {
        source: 'upsell-component',
        itemCount: 1,
        variantId: variantId,
        productId: productId,
        sections: response.sections,
      }));
    }

  } catch (error) {
    console.error("Upsell error:", error);

    document.dispatchEvent(new CartAddEvent({}, variantId, {
      didError: true,
      source: 'upsell-component',
      itemCount: 0,
      variantId: variantId,
      productId: productId,
      error: error.message
    }));
  } finally {
    if (spinner) spinner.hidden = true;
    if (plusIcon) plusIcon.hidden = false;
  }
}

async function refreshCart() {
  try {
    await fetchJson("/cart.js");
  } catch (err) {
    console.warn("Cart refresh failed but continuing anyway:", err);
  }
}

function loadRecommendations(event) {
  requestAnimationFrame(() => {
    const container = document.getElementById("cart-recommendations");
    if (!container) return;
    
    let url = container.dataset.url;

    if (event.detail?.data?.productId) {
      const newProductId = event.detail.data.productId;
      const [path, query] = url.split('?');
      const params = new URLSearchParams(query);
      params.set('product_id', newProductId);
      url = `${path}?${params.toString()}`;
    }
    
    fetch(url)
      .then((res) => res.text())
      .then((html) => {
        const temp = document.createElement("div");
        temp.innerHTML = html;
        const section = temp.querySelector(".product-recommendations");
     
        if (section && section.innerHTML.trim().length) {
          container.innerHTML = section.innerHTML;
          
          const accordion = container.querySelector('.accordion');
          if (accordion) {
            document.dispatchEvent(
              new CustomEvent("accordion:init", { detail: { accordion } })
            );
          }
        } else {
          container.remove();
        }
      })
      .catch((err) => {
        console.error("Recommendations error:", err);
        container.remove();
      });
  })
}

document.addEventListener("DOMContentLoaded", loadRecommendations);
document.addEventListener("cart:update", loadRecommendations);
document.addEventListener("refresh-cart", loadRecommendations);
